import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import AiAnalysisCard from '../components/AiAnalysisCard';
import { useDailyAiCards } from '../hooks/useDailyAiCards';
import api from '../services/api';
import { formatDisplayDate, formatMatchDateTime } from '../utils/time';

const PREPARING_LABEL = 'Bekleniyor';
const TARGET_ANALYSIS = 40;
const ISTANBUL_TIMEZONE = 'Europe/Istanbul';
const STATUS_ARCHIVE_FILTERS = new Set(['won', 'lost', 'void']);

const CATEGORY_FILTERS = [
  { key: 'all', label: 'Tum Analizler' },
  { key: 'MAIN_PICK', label: 'Sistem Tercihi' },
  { key: 'STRONG_TREND', label: 'Guclu Model Egilimi' },
  { key: 'PREMIUM_CANDIDATE', label: 'Premium Kupon Adayi' },
  { key: 'VALUE_BET', label: 'Value Bet' },
  { key: 'RISKY_PICK', label: 'Riskli Secim' }
];

const STATUS_FILTERS = [
  { key: 'all', label: 'Tum Durumlar' },
  { key: 'pending', label: 'Beklemede' },
  { key: 'won', label: 'Tuttu' },
  { key: 'lost', label: 'Tutmadi' },
  { key: 'void', label: 'Gecersiz' }
];

const GRADE_FILTERS = [
  { key: 'all', label: 'Tum Siniflar' },
  { key: 'A', label: 'A' },
  { key: 'B', label: 'B' },
  { key: 'C', label: 'C' },
  { key: 'D', label: 'D' }
];

const normalizeKey = (value = '') => String(value || '').trim().toUpperCase();
const getIstanbulDateKey = (reference = new Date()) => new Intl.DateTimeFormat('en-CA', {
  timeZone: ISTANBUL_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
}).format(reference);
const getMsUntilNextIstanbulMidnight = () => {
  const parts = new Intl.DateTimeFormat('sv-SE', {
    timeZone: ISTANBUL_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).formatToParts(new Date()).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});

  const localNow = new Date(`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}+03:00`);
  const dayStart = new Date(`${parts.year}-${parts.month}-${parts.day}T00:00:00+03:00`);
  const nextMidnight = new Date(dayStart.getTime() + (24 * 60 * 60 * 1000) + 1000);
  return Math.max(1000, nextMidnight.getTime() - localNow.getTime());
};

const buildRecentDateKeys = (anchorDateKey, days = 14) => {
  const baseDate = new Date(`${String(anchorDateKey || getIstanbulDateKey()).trim()}T00:00:00+03:00`);
  if (Number.isNaN(baseDate.getTime())) return [];

  return Array.from({ length: days }, (_, index) => {
    const value = new Date(baseDate.getTime() - (index * 24 * 60 * 60 * 1000));
    return getIstanbulDateKey(value);
  });
};

const getTeamName = (team, fallback = '-') => {
  if (typeof team === 'string' && team.trim()) return team.trim();
  if (team && typeof team === 'object' && typeof team.name === 'string' && team.name.trim()) {
    return team.name.trim();
  }
  return fallback;
};

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const getTurkeyOdds = (card = {}) => {
  const direct = card?.turkishOdds ?? card?.turkeyOdds ?? card?.trOdds ?? card?.localOdds;
  const numeric = Number(direct);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const buildHistoryOptionLabel = (option = {}) => {
  const dateLabel = option?.dateKey ? formatDisplayDate(option.dateKey) : 'Bekleniyor';

  if (option?.isToday && option?.hasPublication) {
    return `${dateLabel} - Bugun`;
  }

  if (option?.isToday) {
    return `${dateLabel} - Bugun (bekleniyor)`;
  }

  if (option?.hasPublication) {
    return `${dateLabel} - ${option.cardCount || 0} analiz`;
  }

  return `${dateLabel} - Yayin yok`;
};

const extractCards = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.cards)) return payload.cards;
  if (Array.isArray(payload.analyses)) return payload.analyses;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.items)) return payload.items;
  if (payload.data && Array.isArray(payload.data.cards)) return payload.data.cards;
  if (payload.data && Array.isArray(payload.data.analyses)) return payload.data.analyses;
  return [];
};

const isAnalysisOnlyCard = (card = {}) => {
  const category = normalizeKey(card?.category);
  const marketFamily = normalizeKey(card?.marketFamily);
  const status = String(card?.status || '').trim().toLowerCase();

  return status === 'analysis_only' || category === 'ANALYSIS_ONLY' || marketFamily === 'ANALYSIS_ONLY';
};

const getCategoryLabel = (card = {}) => {
  const category = normalizeKey(card?.category);
  const isValueBet = Boolean(card?.valueBet) || Number(card?.edge || 0) >= 3;

  if (isAnalysisOnlyCard(card)) return 'Takip Listesi';
  if (category === 'MAIN_PICK') return 'Sistem Tercihi';
  if (category === 'STRONG_TREND') return 'Guclu Model Egilimi';
  if (category === 'RISKY_PICK') return 'Riskli Secim';
  if (isValueBet) return 'Value Bet';
  return 'Premium Kupon Adayi';
};

const isValueBetCard = (card = {}) => Boolean(card?.valueBet) || Number(card?.edge || 0) >= 3;

const filterByStatus = (cards, statusFilter) => {
  if (statusFilter === 'all') return cards;
  return cards.filter((card) => {
    const status = String(card?.predictionStatus || 'pending').trim().toLowerCase();
    return status === statusFilter;
  });
};

const filterByCategory = (cards, categoryFilter) => {
  if (categoryFilter === 'all') return cards;

  return cards.filter((card) => {
    const category = normalizeKey(card?.category);

    if (categoryFilter === 'VALUE_BET') {
      return isValueBetCard(card);
    }

    if (categoryFilter === 'PREMIUM_CANDIDATE') {
      return !isAnalysisOnlyCard(card) && !['MAIN_PICK', 'STRONG_TREND', 'RISKY_PICK'].includes(category) && !isValueBetCard(card);
    }

    return category === normalizeKey(categoryFilter);
  });
};

const applyAnalysisFilters = (cards = [], {
  categoryFilter = 'all',
  statusFilter = 'all',
  gradeFilter = 'all',
  leagueFilter = 'all'
} = {}) => {
  const byCategory = filterByCategory(cards, categoryFilter);
  const byStatus = filterByStatus(byCategory, statusFilter);
  const byGrade = gradeFilter === 'all'
    ? byStatus
    : byStatus.filter((card) => normalizeKey(card?.qualityGrade) === normalizeKey(gradeFilter));

  return leagueFilter === 'all'
    ? byGrade
    : byGrade.filter((card) => card.leagueName === leagueFilter);
};

const getLeagueOptions = (cards) => {
  const map = new Map();

  cards.forEach((card) => {
    const league = card?.leagueName || '-';
    if (!league || league === '-') return;
    const flag = card?.flagEmoji || '';
    map.set(league, `${flag} ${league}`.trim());
  });

  return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
};

const sortCards = (cards = []) => [...cards].sort((left, right) => {
  const leftLive = Boolean(left?.isLive);
  const rightLive = Boolean(right?.isLive);
  if (leftLive !== rightLive) return leftLive ? -1 : 1;

  const leftStatus = String(left?.predictionStatus || 'pending').trim().toLowerCase();
  const rightStatus = String(right?.predictionStatus || 'pending').trim().toLowerCase();
  const leftTime = new Date(left?.startTime || left?.matchDate || 0).getTime();
  const rightTime = new Date(right?.startTime || right?.matchDate || 0).getTime();
  const leftStarted = leftTime <= Date.now();
  const rightStarted = rightTime <= Date.now();
  const weight = (status, started) => {
    if (status === 'pending' && !started) return 0;
    if (status === 'pending' && started) return 1;
    if (status === 'won') return 2;
    if (status === 'lost') return 3;
    return 4;
  };

  const diff = weight(leftStatus, leftStarted) - weight(rightStatus, rightStarted);
  if (diff !== 0) return diff;
  return leftTime - rightTime;
});

const getCardArchiveDateKey = (card = {}, fallback = '') => {
  const rawValue =
    card?.analysisDate ||
    card?.dateKey ||
    card?.publicationDate ||
    card?.matchDate ||
    card?.startTime ||
    card?.matchTime ||
    fallback;

  if (!rawValue) return fallback || '';
  const normalized = String(rawValue).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return normalized;
  }

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return fallback || '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getCardMatchDateKey = (card = {}) => {
  const rawValue =
    card?.startTime ||
    card?.matchDate ||
    card?.matchTime ||
    card?.date ||
    card?.fixtureDate ||
    card?.scheduled ||
    card?.scheduledAt ||
    card?.eventDate ||
    card?.event_date ||
    card?.gameDate ||
    card?.game_date ||
    card?.fixture?.date ||
    card?.match?.date ||
    card?.match?.fixture?.date ||
    '';

  if (!rawValue) return '';
  const normalized = String(rawValue).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return '';
  return getIstanbulDateKey(date);
};

const isTodayMatchCard = (card = {}, currentDateKey = getIstanbulDateKey()) => {
  const matchDateKey = getCardMatchDateKey(card);
  const publicationKey =
    card?.analysisDate ||
    card?.dateKey ||
    card?.publicationDate ||
    card?.publishedDate ||
    '';

  if (matchDateKey) return matchDateKey === currentDateKey;

  const normalizedPublicationKey = String(publicationKey || '').slice(0, 10);
  if (normalizedPublicationKey === currentDateKey) return true;

  return false;
};

const buildPublicationStats = (cards = []) => cards.reduce((acc, card) => {
  acc.totalCards += 1;
  if (card?.premiumEligible) acc.premiumCards += 1;
  if (isValueBetCard(card)) acc.valueBets += 1;

  const status = String(card?.predictionStatus || 'pending').trim().toLowerCase();
  if (status === 'won') acc.won += 1;
  else if (status === 'lost') acc.lost += 1;
  else if (status === 'void') acc.void += 1;
  else acc.pending += 1;

  return acc;
}, {
  totalCards: 0,
  premiumCards: 0,
  valueBets: 0,
  pending: 0,
  won: 0,
  lost: 0,
  void: 0
});

const normalizeAnalysisCard = (card = {}) => {
  const safeCard = card || {};
  const status = String(safeCard.predictionStatus || 'pending').trim().toLowerCase() || 'pending';
  const leagueName = safeCard?.league?.name || safeCard?.leagueName || '-';
  const analysisOnly = isAnalysisOnlyCard(safeCard);
  const matchStatus = String(
    safeCard?.matchStatus ||
    safeCard?.matchStatusShort ||
    safeCard?.match?.status?.short ||
    'NS'
  ).trim().toUpperCase() || 'NS';
  const isLive = Boolean(
    safeCard?.isLive ??
    ['1H', 'HT', '2H', 'ET', 'BT', 'P', 'LIVE'].includes(matchStatus)
  );

  return {
    ...safeCard,
    analysisOnly,
    predictionStatus: status,
    leagueName,
    categoryLabel: safeCard?.level || getCategoryLabel(safeCard),
    flagEmoji: safeCard?.flagEmoji || '',
    homeTeamName: getTeamName(safeCard?.homeTeam, getTeamName(safeCard?.home, '-')),
    awayTeamName: getTeamName(safeCard?.awayTeam, getTeamName(safeCard?.away, '-')),
    market: safeCard?.market || safeCard?.selection || '',
    marketLabel: analysisOnly
      ? 'Net market onerisi yok'
      : (safeCard?.selection || safeCard?.market?.name || safeCard?.market || 'Net market onerisi yok'),
    modelProbability: toNumber(safeCard?.modelProbability ?? safeCard?.model ?? safeCard?.confidence ?? safeCard?.dataQuality),
    confidence: toNumber(safeCard?.confidence ?? safeCard?.dataQuality),
    edge: toNumber(safeCard?.edge),
    odds: getTurkeyOdds(safeCard),
    turkishOdds: getTurkeyOdds(safeCard),
    impliedProbability: toNumber(safeCard?.turkeyImpliedProbability ?? safeCard?.impliedProbability),
    probabilityGrid: safeCard?.probabilityGrid || null,
    teamMetrics: safeCard?.teamMetrics || null,
    homeXg: toNumber(safeCard?.homeXg ?? safeCard?.teamMetrics?.home?.xg),
    homeXga: toNumber(safeCard?.homeXga ?? safeCard?.teamMetrics?.home?.xga),
    awayXg: toNumber(safeCard?.awayXg ?? safeCard?.teamMetrics?.away?.xg),
    awayXga: toNumber(safeCard?.awayXga ?? safeCard?.teamMetrics?.away?.xga),
    homeMetricSource: safeCard?.homeMetricSource || safeCard?.teamMetrics?.home?.metricSource || null,
    awayMetricSource: safeCard?.awayMetricSource || safeCard?.teamMetrics?.away?.metricSource || null,
    scorePrediction: safeCard?.scorePrediction || null,
    startTime: safeCard?.startTime || safeCard?.matchDate || null,
    finalScore: safeCard?.finalScore || null,
    qualityGrade: String(safeCard?.qualityGrade || 'B').trim() || 'B',
    analysis: safeCard?.analysis || '',
    insight: safeCard?.aiInsight || safeCard?.reason || safeCard?.evidence || '',
    reason: safeCard?.reason || safeCard?.evidence || '',
    tag: safeCard?.tag || '',
    fixtureId: safeCard?.fixtureId || safeCard?.apiFixtureId || safeCard?.matchId || safeCard?.id || null,
    isLive,
    matchStatus,
    matchStatusLabel: safeCard?.matchStatusLabel || (isLive ? 'Canli' : (matchStatus === 'NS' || matchStatus === 'TBD' ? 'Yaklasan' : matchStatus))
  };
};

const Analysis = () => {
  const { isAdmin } = useAuth();
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [leagueFilter, setLeagueFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [selectedDateKey, setSelectedDateKey] = useState(getIstanbulDateKey());
  const [archiveSections, setArchiveSections] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [settlementRefreshing, setSettlementRefreshing] = useState(false);
  const [settlementMessage, setSettlementMessage] = useState('');

  const {
    cards,
    stats,
    summary,
    todayKey,
    publicationDate,
    lastUpdated,
    source,
    message,
    error,
    debug,
    history,
    adminNotice,
    loading,
    endpoint,
    fetchDebug
  } = useDailyAiCards(selectedDateKey);

  const refreshSettlementResults = async ({ silent = false } = {}) => {
    if (settlementRefreshing) return null;

    try {
      if (!silent) {
        setSettlementRefreshing(true);
        setSettlementMessage('Sonuclar kontrol ediliyor...');
      }

      const response = await api.post('/analysis/settlement/refresh', {
        syncWindowDays: 14,
        includeRecentFinished: true
      });

      const payload = response?.data || {};
      const updated = Number(payload.updated || payload.settled || 0);

      if (!silent) {
        setSettlementMessage(
          updated > 0
            ? `${updated} analiz sonucu guncellendi.`
            : 'Yeni sonuclanan analiz bulunmadi.'
        );
      }

      if (updated > 0) {
        window.setTimeout(() => window.location.reload(), silent ? 250 : 700);
      }

      return payload;
    } catch (error) {
      if (!silent) {
        setSettlementMessage('Sonuc guncelleme basarisiz oldu. Backend log kontrol edilmeli.');
      }
      return null;
    } finally {
      if (!silent) {
        setSettlementRefreshing(false);
      }
    }
  };

  useEffect(() => {
    const timerId = window.setInterval(() => {
      refreshSettlementResults({ silent: true });
    }, 120000);

    return () => window.clearInterval(timerId);
  }, []);

  const normalizedCards = useMemo(
    () => {
      const requestedDateKey = selectedDateKey || getIstanbulDateKey();

      return sortCards(
        (Array.isArray(cards) ? cards : [])
          .map(normalizeAnalysisCard)
          .filter((card) => {
            const archiveDateKey = getCardArchiveDateKey(card, requestedDateKey);
            const matchDateKey = getCardMatchDateKey(card);
            const publicationDateKey = String(
              card?.analysisDate ||
              card?.dateKey ||
              card?.publicationDate ||
              card?.publishedDate ||
              ''
            ).slice(0, 10);

            return (
              archiveDateKey === requestedDateKey ||
              matchDateKey === requestedDateKey ||
              publicationDateKey === requestedDateKey
            );
          })
      );
    },
    [cards, selectedDateKey]
  );
  const visibleCards = useMemo(
    () => normalizedCards,
    [normalizedCards]
  );
  const publicationStats = useMemo(() => buildPublicationStats(visibleCards), [visibleCards]);
  const leagueOptions = useMemo(() => getLeagueOptions(visibleCards), [visibleCards]);

  useEffect(() => {
    if (leagueFilter !== 'all' && !leagueOptions.some((option) => option.value === leagueFilter)) {
      setLeagueFilter('all');
    }
  }, [leagueFilter, leagueOptions]);

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      setSelectedDateKey(getIstanbulDateKey());
    }, getMsUntilNextIstanbulMidnight());

    return () => {
      window.clearTimeout(timerId);
    };
  }, []);

  const filteredCards = useMemo(
    () => applyAnalysisFilters(visibleCards, {
      categoryFilter,
      statusFilter,
      gradeFilter,
      leagueFilter
    }),
    [categoryFilter, gradeFilter, leagueFilter, statusFilter, visibleCards]
  );
  const settledCards = useMemo(
    () => filteredCards.filter((card) => ['won', 'lost', 'void'].includes(String(card?.predictionStatus || 'pending').trim().toLowerCase())),
    [filteredCards]
  );

  const upcomingCards = useMemo(
    () => {
      const pendingCards = filteredCards.filter((card) => {
        const status = String(card?.predictionStatus || 'pending').trim().toLowerCase();
        return !['won', 'lost', 'void'].includes(status);
      });

      return pendingCards.length > 0 ? pendingCards : filteredCards;
    },
    [filteredCards]
  );

  const displayCards = useMemo(
    () => (upcomingCards.length > 0 ? upcomingCards : filteredCards).slice(0, 200),
    [filteredCards, upcomingCards]
  );

  const shouldUseArchiveSections = false;

  const dateOptions = useMemo(() => {
    const currentDateKey = getIstanbulDateKey();
    const historyDates = Array.isArray(history)
      ? history
          .map((item) => String(item?.dateKey || item?.analysisDate || item?.date || '').slice(0, 10))
          .filter(Boolean)
      : [];

    const keys = Array.from(new Set([
      ...buildRecentDateKeys(currentDateKey, 5),
      ...historyDates
    ])).slice(0, 14);

    return keys.map((dateKey, index) => ({
      dateKey,
      isToday: dateKey === currentDateKey || index === 0,
      hasPublication: dateKey === selectedDateKey ? Array.isArray(cards) && cards.length > 0 : true,
      cardCount: dateKey === selectedDateKey && Array.isArray(cards) ? cards.length : 0
    }));
  }, [cards, history, selectedDateKey]);

  useEffect(() => {
    setArchiveSections([]);
    setArchiveLoading(false);
  }, [categoryFilter, gradeFilter, leagueFilter, statusFilter]);

  const archiveCardsCount = useMemo(
    () => archiveSections.reduce((total, section) => total + section.cards.length, 0),
    [archiveSections]
  );
  const showArchiveSections = shouldUseArchiveSections && archiveSections.length > 0;
  const visibleCount = showArchiveSections ? archiveCardsCount : filteredCards.length;

  const hasPublication = normalizedCards.length > 0;
  const resolvedSelectedDateKey = selectedDateKey || getIstanbulDateKey();
  const isArchiveSelection = resolvedSelectedDateKey !== getIstanbulDateKey();
  const selectedDateLabel = resolvedSelectedDateKey ? formatDisplayDate(resolvedSelectedDateKey) : 'Bekleniyor';
  const sourceLabel = hasPublication ? (isArchiveSelection ? 'Arsiv yayini' : 'Bugunku sabit yayin') : 'Veri Yok';
  const publicationHeadline = hasPublication
    ? `AI Analiz her gun 00:30'da otomatik yenilenir. ${selectedDateLabel} tarihinde ${publicationStats.totalCards} premium analiz yayinda.`
    : 'AI Analiz her gun 00:30\'da otomatik yenilenir. Premium analizler hazirlaniyor.';
  const settledCount = publicationStats.won + publicationStats.lost + publicationStats.void;
  const emptyStateNote = error
    ? error
    : isArchiveSelection
      ? 'Secilen tarih icin yayin bulunamadi. Baska bir tarih secerek gecmis analizleri inceleyebilirsiniz.'
      : 'Bugunun premium AI analizleri hazirlaniyor. Sistem gunun kaliteli maclarini kontrol ediyor.';
  const showLoadingScreen = false;
  const kpiCards = [
    {
      label: 'Yayindaki Analiz',
      value: hasPublication ? publicationStats.totalCards : (loading ? PREPARING_LABEL : 0),
      helper: hasPublication ? publicationHeadline : 'Premium analizler hazirlaniyor.'
    },
    {
      label: 'Premium Secim',
      value: hasPublication ? publicationStats.premiumCards : (loading ? PREPARING_LABEL : 0),
      helper: hasPublication ? `${publicationStats.premiumCards} premium secim listede` : 'Premium secimler hazirlaniyor.'
    },
    {
      label: 'Value Bet',
      value: hasPublication ? publicationStats.valueBets : (loading ? PREPARING_LABEL : 0),
      helper: hasPublication ? `${publicationStats.valueBets} kartta value sinyali var` : 'Value bet taramasi suruyor.'
    },
    {
      label: 'Bekleyen / Sonuclanan',
      value: hasPublication ? `${publicationStats.pending} / ${settledCount}` : (loading ? PREPARING_LABEL : '0 / 0'),
      helper: hasPublication ? 'Bekleyen / Sonuclanan kart dagilimi' : 'Kart dagilimi hazirlaniyor.'
    }
  ];
  const adminDebugVisible = isAdmin && Boolean(error);

  return (
    <div className="page analysis-page ai-dashboard-page ai-analysis-scope ai-premium-layout-v2">
      <section className="hero-panel ai-dashboard-hero ai-premium-hero-v2">
        <div className="ai-hero-grid">
          <div className="hero-copy ai-hero-copy">
            <span className="hero-badge">BUGUNUN EN IYI ANALIZLERI</span>
            <h1 className="live-hero-title">AI ANALIZ MERKEZI</h1>
            <p>Sadece veri kalitesi yuksek maclar analiz edilir. Dusuk veri maclarinda sahte tahmin uretilmez.</p>
            <div className="ai-hero-callouts">
              <span className="ai-callout-pill">{publicationHeadline}</span>
              {resolvedSelectedDateKey ? (
                <span className="ai-callout-pill">Secilen tarih: {selectedDateLabel}</span>
              ) : null}
              {false ? (
                <span className="ai-callout-pill ai-callout-pill-gold">Bugun icin yayin hazir degil. Son basarili analiz yayini gosteriliyor.</span>
              ) : null}
              {isArchiveSelection ? (
                <span className="ai-callout-pill ai-callout-pill-gold">Arsiv tarihi goruntuleniyor.</span>
              ) : null}
              {showLoadingScreen ? (
                <span className="ai-callout-pill">Otomatik AI yayini bekleniyor.</span>
              ) : null}
              {settlementMessage ? (
                <span className="ai-callout-pill ai-callout-pill-gold">{settlementMessage}</span>
              ) : null}
            </div>
            <button
              type="button"
              className="filter-chip active"
              onClick={() => refreshSettlementResults()}
              disabled={settlementRefreshing}
              style={{ marginTop: 14, width: 'fit-content' }}
            >
              {settlementRefreshing ? 'Sonuclar Guncelleniyor...' : 'Sonuclari Guncelle'}
            </button>
          </div>

          <div className="ai-hero-meta">
            <div className="ai-meta-card">
              <span>Secilen Tarih</span>
              <strong>{resolvedSelectedDateKey ? formatDisplayDate(resolvedSelectedDateKey) : (todayKey ? formatDisplayDate(todayKey) : 'Bekleniyor')}</strong>
            </div>
            <div className="ai-meta-card">
              <span>Yayin Tarihi</span>
              <strong>{publicationDate ? formatDisplayDate(publicationDate) : (todayKey ? formatDisplayDate(todayKey) : 'Bekleniyor')}</strong>
            </div>
            <div className="ai-meta-card">
              <span>Son Guncelleme</span>
              <strong>{lastUpdated ? formatMatchDateTime(lastUpdated) : 'Bekleniyor'}</strong>
            </div>
            <div className="ai-meta-card">
              <span>Kaynak</span>
              <strong>{sourceLabel}</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="content-panel ai-kpi-panel ai-premium-kpi-panel-v2">
        <div className="ai-kpi-grid">
          {kpiCards.map((item) => (
            <article key={item.label} className="terminal-stat ai-kpi-card">
              <span>{item.label}</span>
              <strong>{item.value}</strong>
              <small>{item.helper}</small>
            </article>
          ))}
        </div>
      </section>

      <section className="content-panel ai-filter-panel ai-premium-filter-panel-v2">
          <div className="section-heading ai-grid-heading">
            <div>
              <h2>Premium Analiz Akisi</h2>
              <p>{hasPublication ? publicationHeadline : emptyStateNote}</p>
            </div>
            <div className="ai-grid-heading-note">
              <span>
                {hasPublication
                  ? `${visibleCount} kart gorunuyor`
                  : loading
                    ? 'Bekleniyor'
                    : '0 kart'}
              </span>
            </div>
          </div>

        <div className="ai-filter-stack">
          <div className="mobile-filter-chips ai-filter-row">
            {CATEGORY_FILTERS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`filter-chip ${categoryFilter === item.key ? 'active' : ''}`}
                onClick={() => setCategoryFilter(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="mobile-filter-chips ai-filter-row">
            {STATUS_FILTERS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`filter-chip ${statusFilter === item.key ? 'active' : ''}`}
                onClick={() => setStatusFilter(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="ai-select-grid">
            <div className="ai-league-filter ai-date-filter">
              <label htmlFor="analysis-date-filter">Tarih</label>
              <select
                id="analysis-date-filter"
                value={resolvedSelectedDateKey}
                onChange={(event) => setSelectedDateKey(event.target.value)}
              >
                {dateOptions.length ? dateOptions.map((option) => (
                  <option key={option.dateKey} value={option.dateKey}>{buildHistoryOptionLabel(option)}</option>
                )) : <option value="">Tarih yukleniyor</option>}
              </select>
            </div>

            <div className="ai-league-filter">
              <label htmlFor="analysis-league-filter">Lig</label>
              <select
                id="analysis-league-filter"
                value={leagueFilter}
                onChange={(event) => setLeagueFilter(event.target.value)}
              >
                <option value="all">Tum Ligler</option>
                {leagueOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div className="ai-league-filter">
              <label htmlFor="analysis-grade-filter">Guven Sinifi</label>
              <select
                id="analysis-grade-filter"
                value={gradeFilter}
                onChange={(event) => setGradeFilter(event.target.value)}
              >
                {GRADE_FILTERS.map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      <section className="content-panel ai-analysis-grid-panel ai-premium-grid-panel-v2">
        {loading && !hasPublication ? (
          <div className="premium-empty-card ai-empty-state">
            <h3>Premium AI analizleri hazirlaniyor</h3>
            <p>{emptyStateNote}</p>
            {message ? <small>{message}</small> : null}
          </div>
        ) : hasPublication ? (
          showArchiveSections ? (
            <div className="ai-analysis-sections">
              {archiveSections.map((section) => (
                <div className="ai-analysis-section" key={section.dateKey}>
                  <div className="ai-analysis-section-head">
                    <h3>{statusFilter === 'won' ? 'Tutan Analizler' : statusFilter === 'lost' ? 'Tutmayan Analizler' : 'Gecersiz Analizler'} - {section.label}</h3>
                    <span>{section.cards.length} kart</span>
                  </div>
                  <div className="analysis-grid ai-analysis-grid">
                    {section.cards.map((card) => (
                      <AiAnalysisCard
                        key={card.id || card._id || card.fallbackKey || `${section.dateKey}-${card.fixtureId || card.matchId}-${card.market}`}
                        card={card}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : filteredCards.length > 0 ? (
            <div className="ai-analysis-sections">
              {settledCards.length > 0 ? (
                <div className="ai-analysis-section ai-settled-section ai-premium-section-v2">
                  <div className="ai-analysis-section-head">
                    <h3>Sonuclanan Mac Analizleri</h3>
                    <span>{settledCards.length} kart</span>
                  </div>
                  <div className="analysis-grid ai-analysis-grid">
                    {settledCards.map((card) => (
                      <AiAnalysisCard
                        key={card.id || card._id || `${card.fixtureId || card.matchId}-${card.market}`}
                        card={card}
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {displayCards.length > 0 ? (
                <div className="ai-analysis-section ai-upcoming-section ai-premium-section-v2">
                  <div className="ai-analysis-section-head">
                    <h3>{isArchiveSelection ? 'Tarih Secimine Ait Analizler' : 'Yaklasan Mac Analizleri'}</h3>
                    <span>{displayCards.length} kart</span>
                  </div>
                  <div
                    className="analysis-grid ai-analysis-grid ta-ai-visible-grid"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
                      gap: '18px',
                      alignItems: 'stretch',
                      width: '100%'
                    }}
                  >
                    {displayCards.map((card, index) => (
                      <AiAnalysisCard
                        key={card.id || card._id || `${card.fixtureId || card.matchId || index}-${card.selection || card.market || index}`}
                        card={card}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : archiveLoading ? (
            <div className="premium-empty-card ai-empty-state">
              <h3>Gecmis tarihler yukleniyor</h3>
              <p>Secilen durum icin onceki yayin gunleri taraniyor.</p>
            </div>
          ) : (
            <div className="premium-empty-card ai-empty-state">
              <h3>Filtrelerde analiz bulunamadi</h3>
              <p>Secilen kategori, durum veya lig filtrelerinde gosterilecek kart yok. Tutan ve tutmayan analizler artik yayin tarihine gore ayri listelenir; farkli bir tarih veya durum secerek arsivi inceleyebilirsiniz.</p>
            </div>
          )
        ) : (
          <div className="premium-empty-card ai-empty-state">
            <h3>Premium AI analizleri hazirlaniyor</h3>
            <p>{emptyStateNote}</p>
            {message ? <small>{message}</small> : null}
          </div>
        )}
      </section>

      {adminDebugVisible ? (
        <section className="content-panel ai-admin-debug-panel">
          <div className="analysis-header">
            <span>Admin Debug</span>
            <span className="confidence high">{sourceLabel}</span>
          </div>
          <div className="ai-admin-debug-grid">
            <div className="ai-admin-debug-item">
              <span>Hata</span>
              <strong>{error || '-'}</strong>
            </div>
            <div className="ai-admin-debug-item">
              <span>Admin Notu</span>
              <strong>{adminNotice || '-'}</strong>
            </div>
            <div className="ai-admin-debug-item">
              <span>Backend Stats</span>
              <strong>
                {hasPublication ? `${stats?.totalCards || publicationStats.totalCards} / ${summary?.targetCards || TARGET_ANALYSIS}` : PREPARING_LABEL}
              </strong>
            </div>
            <div className="ai-admin-debug-item">
              <span>Secilen / Yayin</span>
              <strong>{resolvedSelectedDateKey || '-'} / {publicationDate || '-'}</strong>
            </div>
            <div className="ai-admin-debug-item">
              <span>Job</span>
              <strong>{debug ? `${debug.lastJobStatus || '-'} / ${debug.lastJobStep || '-'}` : '-'}</strong>
            </div>
            <div className="ai-admin-debug-item">
              <span>Pipeline</span>
              <strong>{debug ? `${debug.rawFixturesCount || 0} / ${debug.dataQualityPassed || 0} / ${debug.publishedCards || 0}` : '-'}</strong>
            </div>
            <div className="ai-admin-debug-item">
              <span>Endpoint</span>
              <strong>{endpoint || '-'}</strong>
            </div>
            <div className="ai-admin-debug-item">
              <span>Payload Keys</span>
              <strong>{Array.isArray(fetchDebug?.payloadKeys) && fetchDebug.payloadKeys.length ? fetchDebug.payloadKeys.join(', ') : '-'}</strong>
            </div>
            <div className="ai-admin-debug-item">
              <span>Extracted Cards</span>
              <strong>{Number(fetchDebug?.extractedCards || 0)}</strong>
            </div>
            <div className="ai-admin-debug-item">
              <span>First Card</span>
              <strong>{fetchDebug?.firstCardSample ? JSON.stringify(fetchDebug.firstCardSample).slice(0, 120) : '-'}</strong>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default Analysis;
